import { computed } from 'vue';
import { useRoute } from 'vue-router';

interface FormSettings {
  parameters?: Array<{
    name: string;
    description?: string;
  }>;
}

interface FormElement {
  id: string;
  type: string;
  props: {
    default_value?: string | string[];
    choices?: Array<{
      id: string;
      content: string;
    }>;
  };
}

interface RouteParams {
  [key: string]: string;
}

const SPECIAL_VALUES = {
  USER_IP: () => 'USER_IP', // This would be replaced with actual IP fetching
  CURRENT_DATE: () => new Date().toISOString().split('T')[0],
  CURRENT_TIME: () => new Date().toTimeString().split(' ')[0],
  CURRENT_DATETIME: () => new Date().toISOString(),
};

export function useDynamicDefaults(routeParams?: RouteParams) {
  const route = useRoute();

  const isDynamicValue = (value?: string | string[]): boolean => {
    if (!value) return false;
    if (Array.isArray(value)) {
      return value.some((v) => v.startsWith('{{') && v.endsWith('}}'));
    }
    return value.startsWith('{{') && value.endsWith('}}');
  };

  const extractDynamicKey = (value: string): string => {
    return value.slice(2, -2).trim();
  };

  const getParameterValue = (
    parameterName: string,
    formSettings: FormSettings
  ): string | undefined => {
    // First check if the parameter exists in the provided route params
    if (routeParams && parameterName in routeParams) {
      return routeParams[parameterName];
    }

    // Then check if it exists in the current route query
    if (route?.query && parameterName in route.query) {
      return route.query[parameterName] as string;
    }

    // Finally check if it exists in form settings
    const parameter = formSettings.parameters?.find((p) => p.name === parameterName);

    if (parameter?.name) {
      // Try to get the value from route query using the parameter name
      if (route?.query && parameter.name in route.query) {
        return route.query[parameter.name] as string;
      }
    }

    return undefined;
  };

  const getSpecialValue = (key: string): string | undefined => {
    const handler = SPECIAL_VALUES[key as keyof typeof SPECIAL_VALUES];
    return handler ? handler() : undefined;
  };

  const resolveDynamicValue = (
    defaultValue: string | string[] | undefined,
    formSettings: FormSettings
  ): string => {
    if (!defaultValue || !isDynamicValue(defaultValue)) {
      return Array.isArray(defaultValue) ? defaultValue[0] || '' : defaultValue || '';
    }

    const value = Array.isArray(defaultValue) ? defaultValue[0] : defaultValue;
    const key = extractDynamicKey(value);

    // First check if it's a parameter reference
    const paramValue = getParameterValue(key, formSettings);

    // If we found a parameter value (even empty string), return it
    if (paramValue !== undefined) {
      return paramValue;
    }

    // Then check if it's a special value
    const specialValue = getSpecialValue(key);
    if (specialValue !== undefined) {
      return specialValue;
    }

    // If it's a known parameter but no value found, return empty string
    if (formSettings.parameters?.some((p) => p.name === key)) {
      return '';
    }

    // If we get here, we couldn't resolve the value at all
    return '';
  };

  return {
    isDynamicValue,
    extractDynamicKey,
    resolveDynamicValue,
    SPECIAL_VALUES: Object.keys(SPECIAL_VALUES),
  };
}
